// owner: social-graph domain agent
import { z } from 'zod';
import {
  EpochMsSchema,
  PaginationQuerySchema,
  UlidSchema,
  UrlSchema,
  VisibilitySchema,
  paginated,
} from './common';
import { PersonRefSchema } from './network';

export * from '../feed-ranking';

export const ReactionKindSchema = z.enum(['like', 'celebrate', 'insightful', 'support', 'curious']);
export type ReactionKind = z.infer<typeof ReactionKindSchema>;

/** An image previously uploaded via POST /feed/images, attached with its required alt text. */
export const PostImageAttachSchema = z.object({
  mediaId: UlidSchema,
  alt: z.string().trim().min(1).max(500),
});

export const PostInputSchema = z
  .object({
    /** Required unless `repostOf` is set: a plain repost (no quote text) posts an empty body. */
    body: z.string().trim().max(3000).default(''),
    linkUrl: UrlSchema.max(2048).nullish(),
    visibility: VisibilitySchema.default('public'),
    seoEnabled: z.boolean().default(true),
    geoEnabled: z.boolean().default(true),
    orgId: UlidSchema.nullish(),
    /** Up to 4 images, previously uploaded via POST /feed/images by the same caller. */
    images: z.array(PostImageAttachSchema).max(4).optional(),
    /** Repost (or quote, when `body` is non-empty) of an existing, visible post. */
    repostOf: UlidSchema.optional(),
    /** When set, the post is invisible everywhere (except to its author) until this instant. */
    publishAt: EpochMsSchema.optional(),
  })
  .refine((v) => v.repostOf !== undefined || v.body.length > 0, {
    message: 'body is required unless repostOf is set (a plain repost)',
    path: ['body'],
  })
  .refine((v) => v.publishAt === undefined || v.publishAt > Date.now(), {
    message: 'publishAt must be strictly in the future',
    path: ['publishAt'],
  });
export const PostPatchSchema = z
  .object({
    body: z.string().trim().min(1).max(3000),
    linkUrl: UrlSchema.max(2048).nullable(),
    visibility: VisibilitySchema,
    seoEnabled: z.boolean(),
    geoEnabled: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Provide at least one field to change');

export const PostRepostOfSchema = z.object({
  id: UlidSchema,
  author: PersonRefSchema,
  body: z.string(),
});
export type PostRepostOf = z.infer<typeof PostRepostOfSchema>;

export const PostSchema = z.object({
  id: UlidSchema,
  author: PersonRefSchema,
  orgId: UlidSchema.nullable(),
  body: z.string(),
  linkUrl: z.string().nullable(),
  visibility: VisibilitySchema,
  seoEnabled: z.boolean(),
  geoEnabled: z.boolean(),
  viaAgent: z.boolean(),
  counts: z.object({ reactions: z.number().int(), comments: z.number().int() }),
  viewerReaction: ReactionKindSchema.nullable(),
  /** Attached images, in attach order; `url` is `/api/v1/feed/images/{mediaId}`. */
  images: z.array(z.object({ url: z.string(), alt: z.string() })),
  /** True for a plain repost (repostOf set, body empty) as opposed to a quote (body non-empty). */
  isRepost: z.boolean(),
  repostOf: PostRepostOfSchema.nullable(),
  /** User ids of resolved @handle mentions in `body`. */
  mentions: z.array(UlidSchema),
  /** Lowercased, deduped #hashtags found in `body`. */
  hashtags: z.array(z.string()),
  publishAt: EpochMsSchema.nullable(),
  createdAt: EpochMsSchema,
  updatedAt: EpochMsSchema,
});
export type Post = z.infer<typeof PostSchema>;
export const FeedItemSchema = PostSchema.extend({
  score: z.number(),
  relation: z.enum(['self', 'follow', 'connection', 'both']),
});
export const FeedResponseSchema = paginated(FeedItemSchema);
export const PostListSchema = paginated(PostSchema);
export const PostIdParamsSchema = z.object({ id: UlidSchema });
export const PostListQuerySchema = PaginationQuerySchema.extend({
  author: z.string().trim().min(1).max(60).optional(),
});

export const POST_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const POST_IMAGE_TYPES = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
} as const;
export const PostImageResponseSchema = z.object({
  mediaId: UlidSchema,
  url: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int(),
});
export const PostImageParamsSchema = z.object({ mediaId: UlidSchema });

export const HashtagSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9_]{2,50}$/, 'Hashtag: 2-50 chars a-z 0-9 _');
export const HashtagParamsSchema = z.object({ tag: HashtagSchema });

export const CommentInputSchema = z.object({
  body: z.string().trim().min(1).max(1500),
  parentId: UlidSchema.nullish(),
});
export const CommentSchema = z.object({
  id: UlidSchema,
  postId: UlidSchema,
  parentId: UlidSchema.nullable(),
  author: PersonRefSchema,
  body: z.string(),
  counts: z.object({ reactions: z.number().int() }),
  viewerReaction: ReactionKindSchema.nullable(),
  createdAt: EpochMsSchema,
});
export const CommentListSchema = paginated(CommentSchema);
export const CommentListQuerySchema = PaginationQuerySchema;

export const ReactionInputSchema = z.object({ kind: ReactionKindSchema });
export const ReactionStateSchema = z.object({
  kind: ReactionKindSchema.nullable(),
  reactions: z.number().int(),
});
